import type { OrgPermission, ProjectPermission } from "./types";

/**
 * The single source of truth for which permission gates each project feature.
 * Each entry's `read` gates both the feature's nav item (hide) and its route
 * (redirect); the write verbs gate create/update/delete actions inside the feature.
 *
 * Reference dictionary entries everywhere, never a raw permission string, so the
 * nav item and the route guard can't drift, and `satisfies` fails the build on a
 * typo or a token the backend dropped. Mirrors the project tokens in
 * `arcade/authz/permissions.py`.
 *
 * Org-scoped features use the separate `ORG` dictionary below.
 */
export const PROJECT = {
  projectMetadata: {
    read: "project_metadata_read",
    update: "project_metadata_update",
  },
  securitySettings: {
    read: "security_settings_read",
    update: "security_settings_update",
  },
  members: {
    read: "members_read",
    update: "members_update",
    delete: "members_delete",
  },
  invitations: {
    read: "invitations_read",
    create: "invitations_create",
    delete: "invitations_delete",
  },
  apiKeys: {
    read: "api_keys_read",
    create: "api_keys_create",
    update: "api_keys_update",
    delete: "api_keys_delete",
  },
  auditLogs: { read: "audit_logs_read" },
  tools: { read: "tools_read", execute: "tools_execute" },
  servers: {
    read: "servers_read",
    create: "servers_create",
    update: "servers_update",
    delete: "servers_delete",
  },
  deployments: {
    read: "deployments_read",
    create: "deployments_create",
    update: "deployments_update",
    delete: "deployments_delete",
  },
  authProviders: {
    read: "auth_providers_read",
    create: "auth_providers_create",
    update: "auth_providers_update",
    delete: "auth_providers_delete",
  },
  mcpGateways: {
    read: "mcp_gateways_read",
    create: "mcp_gateways_create",
    update: "mcp_gateways_update",
    delete: "mcp_gateways_delete",
  },
  catalog: {
    read: "catalog_read",
    create: "catalog_create",
    update: "catalog_update",
    delete: "catalog_delete",
  },
  secrets: {
    read: "secrets_read",
    create: "secrets_create",
    update: "secrets_update",
    delete: "secrets_delete",
  },
  userConnections: {
    read: "user_connections_read",
    delete: "user_connections_delete",
  },
  plugins: {
    read: "plugins_read",
    create: "plugins_create",
    update: "plugins_update",
    delete: "plugins_delete",
  },
  hooks: {
    read: "hooks_read",
    create: "hooks_create",
    update: "hooks_update",
    delete: "hooks_delete",
  },
} as const satisfies Record<
  string,
  Partial<
    Record<
      "read" | "create" | "update" | "delete" | "execute",
      ProjectPermission
    >
  >
>;

/**
 * The org counterpart of `PROJECT`: the single source of truth for which permission
 * gates each org-level feature. Same rules apply — reference dictionary entries, never
 * a raw permission string, so `satisfies` fails the build on a typo or a dropped token.
 * Mirrors the org tokens in `arcade/authz/permissions.py`.
 *
 * The org `/capabilities` endpoint is Org-Admin-only today, so org checks fail closed on
 * a fetch error (only Org Admins resolve real org permissions). See `get-capabilities.ts`.
 */
export const ORG = {
  orgMetadata: {
    read: "org_metadata_read",
    update: "org_metadata_update",
  },
  projects: {
    create: "projects_create",
    delete: "projects_delete",
  },
  auditLogs: { read: "audit_logs_read" },
  members: {
    read: "members_read",
    update: "members_update",
    delete: "members_delete",
  },
  authProviders: {
    create: "auth_providers_create",
    update: "auth_providers_update",
    delete: "auth_providers_delete",
  },
  plugins: {
    create: "plugins_create",
    update: "plugins_update",
    delete: "plugins_delete",
  },
  hooks: {
    create: "hooks_create",
    update: "hooks_update",
    delete: "hooks_delete",
  },
  billing: {
    read: "billing_read",
    update: "billing_update",
  },
} as const satisfies Record<
  string,
  Partial<Record<"read" | "create" | "update" | "delete", OrgPermission>>
>;
